import { useSiteInspectionNavigatorContext } from '@euler/app/flows/task/pages/inspection/site-inspection/SiteInspectionContext';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { Img } from '@euler/components/image/AdvancedImage';
import {
  LayoutProviderView,
  useContainerLayout,
} from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useQueryProductDetail } from '@euler/functions/product/useProductDetail';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { IdType, Size } from '@euler/model/common';
import { ProductDetailContentType } from '@euler/model/enum';
import { ProductAnyImageType } from '@euler/model/product';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ResizeMode, Video } from 'expo-av';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const ProductDetailPage = wrapNavigatorScreen(
  ({ id, subTitleColor = '#777' }: { id: IdType; subTitleColor?: string }) => {
    const navigation = useNavigation<StackNavigationProp<any>>();
    const contentContainerStyle = useScreenContentContainerStyle();
    const siteInspectionNavContext = useSiteInspectionNavigatorContext();
    const bottomComponent$ = siteInspectionNavContext?.bottomComponent$;

    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const detailQuery = useQueryProductDetail(id);

    useEffect(() => {
      if (bottomComponent$ == null) return;
      const bottomComponent = bottomComponent$.value;
      bottomComponent$.next(undefined);
      return () => bottomComponent$.next(bottomComponent);
    }, [bottomComponent$]);

    const ScrollViewComponent =
      siteInspectionNavContext?.navigatorContainerType === 'bottomsheet'
        ? BottomSheetScrollView
        : ScrollView;

    const productName = detailQuery.state?.name;
    useEffect(() => {
      if (productName) {
        navigation.setOptions({
          headerTitle: props => (
            <MultilineHeaderTitle
              {...props}
              subTitleStyle={{
                fontFamily: FontFamily.NotoSans.Medium,
                fontSize: rpx(10),
              }}
            >
              {`产品详情\n!color[${subTitleColor}] ${productName}`}
            </MultilineHeaderTitle>
          ),
        });
      }
    }, [navigation, productName, rpx, subTitleColor]);

    return (
      <ScrollViewComponent
        contentContainerStyle={[
          {
            flexGrow: 1,
            paddingBottom: rpx(68) + bottom,
          },
          contentContainerStyle,
        ]}
      >
        {detailQuery.error ? (
          <View
            css={`
              min-height: ${rpx(400)}px;
              padding: ${rpx(15)}px;
            `}
          >
            <EmptyView
              error={detailQuery.error}
              message={`对不起，查询失败，请稍后重试! (${detailQuery.error.message})`}
              onRetry={detailQuery.invalidate}
              messageStyle={{ fontSize: rpx(14) }}
            />
          </View>
        ) : detailQuery.loading ? (
          <Center
            css={`
              min-height: ${rpx(400)}px;
            `}
          >
            <ActivityIndicator />
          </Center>
        ) : detailQuery.state?.detail?.contents.type ===
          ProductDetailContentType.ImageList ? (
          <LayoutProviderView>
            {detailQuery.state.detail.contents.images.map(x =>
              x.type === ProductAnyImageType.Image ? (
                <ImageContent key={x.id} uri={x.url} />
              ) : (
                <VideoContent key={x.id} uri={x.url} />
              ),
            )}
          </LayoutProviderView>
        ) : null}
      </ScrollViewComponent>
    );
  },
  {
    title: '产品详情',
  },
);

const ImageContent = memo(
  ({ uri, ...props }: { uri: string; style?: StyleProp<ImageStyle> }) => {
    const containerWidth = useContainerLayout().width;
    const [size, setSize] = useState<Size>();
    const style = useMemo<ImageStyle>(
      () => ({
        width: containerWidth,
        height: containerWidth * (size ? size.height / size.width : 0.6),
      }),
      [containerWidth, size],
    );
    const onLoad = useCallback((s: Size) => {
      setSize(s);
    }, []);
    return (
      <Img
        uri={uri}
        resizeMode="cover"
        style={[style, props.style]}
        onLoad={onLoad}
      />
    );
  },
);

const VideoContent = memo(
  ({ uri, ...props }: { uri: string; style?: StyleProp<ViewStyle> }) => {
    const containerWidth = useContainerLayout().width;
    const style = useMemo<ViewStyle>(
      () => ({
        width: containerWidth,
        height: containerWidth * 0.8,
      }),
      [containerWidth],
    );
    return (
      <Video
        source={{ uri }}
        resizeMode={ResizeMode.CONTAIN}
        style={[style, props.style]}
      />
    );
  },
);
